/**
 * useMenuConfiguration Hook
 * Hook per gestire la configurazione menu dinamica basata su company
 */
import { useQuery } from '@tanstack/react-query';
import { useAccount } from '@/providers/account-provider';
import { storage } from '@/lib/local-storage';
import { isPathAccessible, findMenuItemKeyByPath } from '@/config/menu-items';
import { apiClient } from '@/lib/api-client';
import { useMe } from '@/hooks/api/use-auth';

// Menu items che non possono essere disabilitati e sono sempre visibili
const NON_MODIFIABLE_MENU_ITEMS = ['controlHub', 'settings', 'activity'];

interface MenuConfiguration {
    [menuItemKey: string]: boolean;
}

interface MenuConfigurationResponse {
    items: MenuConfiguration;
}

/**
 * Valida se una stringa è un UUID valido
 * UUID format: 8-4-4-4-12 hexadecimal characters
 */
function isValidUUID(str: string | null | undefined): boolean {
    if (!str) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
}

/**
 * Fetcha la configurazione menu da API
 */
async function fetchMenuConfiguration(companyId: string): Promise<MenuConfiguration> {
    try {
        const data = await apiClient<MenuConfigurationResponse>(
            `/menu-configurations/by-company/${companyId}`,
            { requireAuth: true }
        );
        return data.items;
    } catch (error) {
        // Se la chiamata fallisce, ritorniamo una config di default (tutto abilitato)
        console.warn('Failed to fetch menu configuration, using default', error);
        return {};
    }
}

/**
 * Hook principale per la gestione delle configurazioni menu
 */
export function useMenuConfiguration() {
    const { selectedAccountId } = useAccount();
    const { data: currentUser, isLoading: isLoadingUser } = useMe();
    const userType = storage.getItem('userType')?.toLowerCase();
    
    // Gli admin bypassano sempre le restrizioni
    const isAdmin = userType === 'admin';
    
    // Usa company_id dall'utente corrente (UUID) se disponibile, altrimenti fallback a selectedAccountId
    // Nota: selectedAccountId potrebbe essere un ID numerico, quindi preferiamo company_id quando disponibile
    // Se currentUser è ancora in loading, usa null per evitare di usare selectedAccountId che potrebbe non essere un UUID
    const companyId = currentUser?.company_id || (isLoadingUser ? null : selectedAccountId);
    
    // Valida che companyId sia un UUID valido prima di fare la chiamata
    // Se non è un UUID valido, non eseguire la query (evita errori 422)
    const hasValidCompanyId = isValidUUID(companyId);
    
    // Fetch configuration da API
    const { data: menuConfig, isLoading, error } = useQuery<MenuConfiguration>({
        queryKey: ['menu-configuration', companyId],
        queryFn: () => fetchMenuConfiguration(companyId!),
        staleTime: 5 * 60 * 1000, // 5 minuti
        gcTime: 10 * 60 * 1000, // 10 minuti
        enabled: hasValidCompanyId && !isAdmin && !isLoadingUser, // Non fetchare per admin, se companyId non è valido, o se user è ancora in loading
        retry: 1 // Riprova una volta se fallisce
    });
    
    /**
     * Verifica se un menu item è abilitato
     */
    const isMenuItemEnabled = (itemKey: string): boolean => {
        // Admin ha sempre accesso a tutto
        if (isAdmin) {
            return true;
        }
        
        // Menu items non modificabili sono sempre abilitati
        if (NON_MODIFIABLE_MENU_ITEMS.includes(itemKey)) {
            return true;
        }
        
        // Se la config non è ancora caricata o non esiste, abilita tutto di default
        if (!menuConfig || Object.keys(menuConfig).length === 0) {
            return true;
        }
        
        // Verifica lo stato del menu item nella config
        // Se l'item non è presente nella config, considera come abilitato (default behavior)
        return menuConfig[itemKey] !== false;
    };
    
    /**
     * Verifica se un route path è accessibile
     */
    const isRouteAccessible = (path: string): boolean => {
        // Admin ha sempre accesso
        if (isAdmin) {
            return true;
        }
        
        // Se non abbiamo userType, permettiamo l'accesso
        if (!userType) {
            return true;
        }
        
        // Se la config non è caricata, permettiamo l'accesso temporaneamente
        if (isLoading || !menuConfig) {
            return true;
        }
        
        // Usa la helper function per verificare accessibilità
        return isPathAccessible(path, menuConfig, userType);
    };
    
    /**
     * Ritorna la lista di menu items abilitati
     */
    const enabledMenuItems = (): string[] => {
        if (isAdmin || !menuConfig) {
            return [];
        }
        
        return Object.keys(menuConfig).filter(key => menuConfig[key] === true);
    };
    
    /**
     * Verifica se un menu item specifico dovrebbe essere mostrato
     * Considera sia lo stato enabled che eventuali regole business
     */
    const shouldShowMenuItem = (itemKey: string): boolean => {
        return isMenuItemEnabled(itemKey);
    };
    
    /**
     * Ritorna il menu item key per un dato path
     */
    const getMenuItemKeyForPath = (path: string): string | null => {
        if (!userType) {
            return null;
        }
        
        return findMenuItemKeyByPath(path, userType);
    };
    
    return {
        // State
        isLoading,
        error,
        isAdmin,
        menuConfig,
        
        // Functions
        isMenuItemEnabled,
        isRouteAccessible,
        shouldShowMenuItem,
        getMenuItemKeyForPath,
        enabledMenuItems: enabledMenuItems()
    };
}

/**
 * Hook semplificato per verificare solo se un menu item è abilitato
 * Utile nei layout per filtrare le voci di menu
 */
export function useIsMenuItemEnabled(itemKey: string): boolean {
    const { isMenuItemEnabled } = useMenuConfiguration();
    return isMenuItemEnabled(itemKey);
}

/**
 * Hook semplificato per verificare l'accessibilità di un route
 * Utile nei route guards
 */
export function useIsRouteAccessible(path: string): boolean {
    const { isRouteAccessible } = useMenuConfiguration();
    return isRouteAccessible(path);
}

